import type { ProductPassport } from "@/hooks/useProductPassport";
import type { LabelPrintJob, LabelSizeId, PassportLabelData } from "./types";

export interface PassportLabelCopy {
  certifiedHeading: string;
  modelCaption: string;
  categoryCaption: string;
  completedCaption: string;
  qualityGradeCaption: string;
  serialNumberHeading: string;
  scanHint: string;
  locale?: string;
}

export function passportLabelTitle(passport: ProductPassport): string {
  const brand = (passport.brand || "").trim();
  const nameRaw = (passport.appliance_name || passport.category || "").trim();
  const name =
    brand && nameRaw.toLowerCase().startsWith(brand.toLowerCase())
      ? nameRaw.slice(brand.length).trim()
      : nameRaw;
  return [brand || "—", name].filter(Boolean).join(" ");
}

export function passportPublicUrl(passportId: string, origin = window.location.origin): string {
  return `${origin}/passport/${passportId}`;
}

export function passportToLabelData(
  passport: ProductPassport,
  copy: PassportLabelCopy,
): PassportLabelData {
  const completedDate = new Date(passport.completed_at).toLocaleDateString(copy.locale || "en-GB");
  return {
    qrValue: passportPublicUrl(passport.id),
    certifiedHeading: copy.certifiedHeading,
    title: passportLabelTitle(passport),
    modelLine: `${copy.modelCaption}: ${passport.model_number || "—"}`,
    categoryLine: `${copy.categoryCaption}: ${passport.category || "—"}`,
    completedLine: `${copy.completedCaption}: ${completedDate}`,
    qualityGradeLine: passport.quality_grade
      ? `${copy.qualityGradeCaption}: ${passport.quality_grade}`
      : "",
    serialNumberHeading: copy.serialNumberHeading,
    serialNumber: passport.serial_number,
    scanHint: copy.scanHint,
  };
}

/** Build data-oriented print jobs from ProductPassport rows + i18n copy. */
export function buildPassportPrintJobs(
  passports: ProductPassport[],
  copy: PassportLabelCopy,
  size?: LabelSizeId,
): LabelPrintJob<"passport">[] {
  return passports.map((passport) => ({
    kind: "passport" as const,
    data: passportToLabelData(passport, copy),
    options: { size, locale: copy.locale },
  }));
}

/** @deprecated Prefer `buildPassportPrintJobs`. */
export function buildPassportPrintJobsFromPassports(
  passports: ProductPassport[],
  copy: PassportLabelCopy,
  size: LabelSizeId,
): LabelPrintJob<"passport">[] {
  return buildPassportPrintJobs(passports, copy, size);
}
